import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/header';
import {
  TbArrowLeft,
  TbArrowRight,
  TbCamera,
  TbCheck,
  TbChevronDown,
  TbExternalLink,
  TbLink,
  TbList,
  TbPackage,
  TbPhoto,
  TbPlus,
  TbTicket,
  TbTools,
  TbTrash,
  TbX,
} from 'react-icons/tb';
import CategoryFilterPills from '../components/CategoryFilterPills';
import './AddProductPage.css';

const API_BASE = '/api';
const MAX_IMAGES = 10;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

const TYPE_OPTIONS = [
  { value: 'product', label: 'Product', Icon: TbPackage },
  { value: 'service', label: 'Service', Icon: TbTools },
  { value: 'experience', label: 'Experience', Icon: TbTicket },
];

const INITIAL_FORM = {
  itemType: 'product',
  images: [],
  imageUrl: '',
  name: '',
  price: '',
  category: null,
  description: '',
  externalUrl: '',
};

const stepTitles = {
  1: 'New listing',
  2: 'Basics',
  3: 'Details',
};

const slugify = (value) =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const isValidUrl = (value) => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (_) {
    return false;
  }
};

const formatCurrency = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(2) : '0.00';
};

const labelForType = (type) =>
  TYPE_OPTIONS.find((option) => option.value === type)?.label || 'Product';

const categoryFromRecord = (category) => ({
  id: category.id || null,
  slug: category.slug || slugify(category.name),
  name: category.name,
  count: category.count,
  isNew: false,
});

const AddProductPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INITIAL_FORM);
  const [store, setStore] = useState(null);
  const [storeCategories, setStoreCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [itemCounts, setItemCounts] = useState({ product: null, service: null, experience: null });
  const [showImageUrlInput, setShowImageUrlInput] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [primaryImageIndex, setPrimaryImageIndex] = useState(0);
  const [activeThumbIndex, setActiveThumbIndex] = useState(null);
  const [newCategoryOpen, setNewCategoryOpen] = useState(false);
  const [newCategoryDraft, setNewCategoryDraft] = useState('');
  const [externalOpen, setExternalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const descriptionRef = useRef(null);
  const imagesRef = useRef([]);

  const updateForm = useCallback((patch) => {
    setForm((current) => ({ ...current, ...patch }));
  }, []);

  const imageUrlValue = form.imageUrl.trim();
  const hasImageUrlText = imageUrlValue.length > 0;
  const hasValidImageUrl = hasImageUrlText && isValidUrl(imageUrlValue);
  const imageUrlInvalid = hasImageUrlText && !hasValidImageUrl;

  const photoItems = useMemo(() => {
    const fileItems = form.images.map((image, index) => ({
      ...image,
      source: 'file',
      fileIndex: index,
      preview: image.preview,
    }));

    return hasValidImageUrl
      ? [
          ...fileItems,
          {
            id: 'url-image',
            source: 'url',
            name: 'Image URL',
            preview: imageUrlValue,
          },
        ]
      : fileItems;
  }, [form.images, hasValidImageUrl, imageUrlValue]);

  const photoCount = photoItems.length;
  const canContinueStep1 = photoCount > 0 && !imageUrlInvalid;
  const canContinueStep2 =
    form.name.trim().length > 0 &&
    form.price.trim().length > 0 &&
    Number.isFinite(Number(form.price)) &&
    Number(form.price) >= 0;

  const hasEnteredData = useMemo(
    () =>
      form.itemType !== 'product' ||
      form.images.length > 0 ||
      form.imageUrl.trim().length > 0 ||
      form.name.trim().length > 0 ||
      form.price.trim().length > 0 ||
      Boolean(form.category) ||
      form.description.trim().length > 0 ||
      form.externalUrl.trim().length > 0,
    [form]
  );

  const categoryOptions = useMemo(() => {
    const globalBySlug = new Map(
      allCategories.map((category) => [category.slug || slugify(category.name), category])
    );

    const options = storeCategories.map((category) => {
      const slug = category.slug || slugify(category.name);
      const globalMatch = globalBySlug.get(slug);
      return categoryFromRecord({ ...category, id: globalMatch?.id || category.id, slug });
    });

    if (form.category && !options.some((category) => category.slug === form.category.slug)) {
      return [...options, form.category];
    }

    return options;
  }, [allCategories, form.category, storeCategories]);

  const bottomHint = useMemo(() => {
    if (step === 1) {
      if (imageUrlInvalid) return 'Enter a valid image URL, or clear the URL field.';
      if (!canContinueStep1) return 'Add a photo or image URL to continue.';
    }
    if (step === 2) {
      if (!form.name.trim()) return 'Add a name to continue.';
      if (!form.price.trim()) return 'Add a price to continue.';
      if (!canContinueStep2) return 'Enter a valid non-negative price.';
    }
    return '';
  }, [canContinueStep1, canContinueStep2, form.name, form.price, imageUrlInvalid, step]);

  useEffect(() => {
    window.history.replaceState(
      { ...(window.history.state || {}), addProductStep: 1 },
      '',
      window.location.href
    );

    const handlePopState = (event) => {
      const nextStep = event.state?.addProductStep;
      if (nextStep >= 1 && nextStep <= 3) {
        setStep(nextStep);
        setError('');
        setActiveThumbIndex(null);
        window.scrollTo({ top: 0 });
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    imagesRef.current = form.images;
  }, [form.images]);

  useEffect(() => {
    return () => {
      imagesRef.current.forEach((image) => {
        if (image.preview) URL.revokeObjectURL(image.preview);
      });
    };
  }, []);

  useEffect(() => {
    if (primaryImageIndex > Math.max(photoCount - 1, 0)) {
      setPrimaryImageIndex(0);
    }
    if (activeThumbIndex !== null && activeThumbIndex > photoCount - 1) {
      setActiveThumbIndex(null);
    }
  }, [activeThumbIndex, photoCount, primaryImageIndex]);

  useEffect(() => {
    const fetchStore = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await fetch(`${API_BASE}/user/store`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const storeData = await response.json();
          setStore(storeData);
        } else if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('token');
          navigate('/login');
        } else {
          setError('Unable to load your store.');
        }
      } catch (err) {
        console.error('Error fetching store info:', err);
        setError('Unable to load your store.');
      }
    };

    fetchStore();
  }, [navigate]);

  useEffect(() => {
    if (!store?.url_name) return;

    let cancelled = false;
    const loadCategoryData = async () => {
      setCategoriesLoading(true);
      try {
        const [storeCategoryResponse, allCategoryResponse, countsResponse] = await Promise.all([
          fetch(`${API_BASE}/stores/${store.url_name}/category-counts?item_type=product`),
          fetch(`${API_BASE}/categories`),
          fetch(`${API_BASE}/stores/${store.url_name}/item-type-counts`),
        ]);

        const storeCategoryData = storeCategoryResponse.ok
          ? await storeCategoryResponse.json()
          : { categories: [] };
        const allCategoryData = allCategoryResponse.ok ? await allCategoryResponse.json() : [];
        const countsData = countsResponse.ok ? await countsResponse.json() : null;

        if (!cancelled) {
          setStoreCategories(Array.isArray(storeCategoryData.categories) ? storeCategoryData.categories : []);
          setAllCategories(Array.isArray(allCategoryData) ? allCategoryData : []);
          if (countsData) setItemCounts(countsData);
        }
      } catch (err) {
        console.error('Error loading category data:', err);
        if (!cancelled) {
          setStoreCategories([]);
          setAllCategories([]);
        }
      } finally {
        if (!cancelled) setCategoriesLoading(false);
      }
    };

    loadCategoryData();
    return () => {
      cancelled = true;
    };
  }, [store?.url_name]);

  const goToStep = useCallback((nextStep) => {
    setStep(nextStep);
    setError('');
    setSuccess('');
    setActiveThumbIndex(null);
    window.history.pushState(
      { ...(window.history.state || {}), addProductStep: nextStep },
      '',
      window.location.href
    );
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleBackStep = () => {
    if (step > 1) {
      window.history.back();
    }
  };

  const handleClose = () => {
    if (
      hasEnteredData &&
      !window.confirm("Discard this listing? Your changes won't be saved.")
    ) {
      return;
    }
    navigate('/dashboard');
  };

  // Capture-phase guard for clicks on <a href> nav links (e.g. those rendered
  // by the global <Header />). Runs before React Router's <Link> handler so
  // we can cancel the navigation if the user declines the confirm. Compatible
  // with the legacy BrowserRouter — no data-router APIs.
  useEffect(() => {
    if (!hasEnteredData) return undefined;

    const handler = (event) => {
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const anchor = event.target.closest('a[href]');
      if (!anchor) return;
      if (anchor.target === '_blank' || anchor.hasAttribute('download')) return;

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;

      let destination;
      try {
        destination = new URL(anchor.href, window.location.href);
      } catch (_) {
        return;
      }
      if (destination.origin !== window.location.origin) return;
      if (destination.pathname === window.location.pathname) return;

      if (!window.confirm("Discard this listing? Your changes won't be saved.")) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [hasEnteredData]);

  // Browser-level guard: tab close, refresh, typed URL.
  useEffect(() => {
    if (!hasEnteredData) return undefined;

    const handler = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasEnteredData]);

  const handleFiles = useCallback(
    (files) => {
      const incoming = Array.from(files || []);
      const imageFiles = incoming.filter((file) => file.type.startsWith('image/'));

      if (incoming.length > 0 && imageFiles.length !== incoming.length) {
        setError('Please select only image files.');
        return;
      }

      if (imageFiles.length === 0) return;

      const oversized = imageFiles.find((file) => file.size > MAX_IMAGE_SIZE);
      if (oversized) {
        setError('Some files are too large. Maximum size is 5MB per image.');
        return;
      }

      const availableSlots = MAX_IMAGES - photoCount;
      if (imageFiles.length > availableSlots) {
        setError('Maximum 10 images allowed per listing.');
        return;
      }

      const newImages = imageFiles.map((file, index) => ({
        id: `${Date.now()}-${index}`,
        file,
        preview: URL.createObjectURL(file),
        name: file.name,
        size: file.size,
      }));

      setForm((current) => ({ ...current, images: [...current.images, ...newImages] }));
      if (photoCount === 0) setPrimaryImageIndex(0);
      setError('');
    },
    [photoCount]
  );

  const handleFileInput = (event) => {
    handleFiles(event.target.files);
    event.target.value = '';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    handleFiles(event.dataTransfer.files);
  };

  const removePhotoItem = (photoIndex) => {
    const item = photoItems[photoIndex];
    if (!item) return;

    if (item.source === 'url') {
      updateForm({ imageUrl: '' });
    } else {
      setForm((current) => {
        const image = current.images[item.fileIndex];
        if (image?.preview) URL.revokeObjectURL(image.preview);
        return {
          ...current,
          images: current.images.filter((_, index) => index !== item.fileIndex),
        };
      });
    }

    const nextCount = Math.max(photoItems.length - 1, 0);
    setPrimaryImageIndex((current) => {
      if (nextCount === 0) return 0;
      if (current === photoIndex) return 0;
      if (photoIndex < current) return Math.max(current - 1, 0);
      return current;
    });
    setActiveThumbIndex(null);
  };

  const handleContinue = () => {
    if (step === 1 && canContinueStep1) {
      goToStep(2);
      return;
    }

    if (step === 2 && canContinueStep2) {
      goToStep(3);
    }
  };

  const insertMarkdown = (syntax) => {
    const textarea = descriptionRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = form.description;
    const selected = text.substring(start, end);
    let insertion = '';
    let cursorOffset = 0;

    if (syntax === 'bold') {
      insertion = selected ? `**${selected}**` : '**bold text**';
      cursorOffset = selected ? insertion.length : 2;
    } else if (syntax === 'italic') {
      insertion = selected ? `*${selected}*` : '*italic text*';
      cursorOffset = selected ? insertion.length : 1;
    } else if (syntax === 'bullet') {
      const prefix = start > 0 && text[start - 1] !== '\n' ? '\n' : '';
      insertion = `${prefix}• ${selected}`;
      cursorOffset = insertion.length;
    }

    const nextDescription = text.substring(0, start) + insertion + text.substring(end);
    updateForm({ description: nextDescription });

    window.setTimeout(() => {
      textarea.focus();
      const nextCursor = start + cursorOffset;
      textarea.setSelectionRange(nextCursor, nextCursor);
    }, 0);
  };

  const handleCategoryChange = (slug, category) => {
    if (form.category?.slug === slug) {
      updateForm({ category: null });
      return;
    }
    updateForm({ category: categoryFromRecord(category) });
  };

  const commitNewCategory = () => {
    const name = newCategoryDraft.trim();
    if (!name) return;

    const slug = slugify(name);
    const existing =
      categoryOptions.find((category) => category.slug === slug) ||
      allCategories.find(
        (category) =>
          (category.slug || slugify(category.name)) === slug ||
          category.name.toLowerCase() === name.toLowerCase()
      );

    updateForm({
      category: existing
        ? categoryFromRecord(existing)
        : {
            id: null,
            slug,
            name,
            isNew: true,
          },
    });
    setNewCategoryDraft('');
    setNewCategoryOpen(false);
  };

  const orderedImagesForSubmit = () => {
    const primaryItem = photoItems[primaryImageIndex];
    const images = [...form.images];

    if (primaryItem?.source === 'file' && primaryItem.fileIndex > 0) {
      const [primaryImage] = images.splice(primaryItem.fileIndex, 1);
      images.unshift(primaryImage);
    }

    return images;
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    if (!canContinueStep2) {
      goToStep(2);
      return;
    }

    if (form.externalUrl.trim() && !isValidUrl(form.externalUrl.trim())) {
      setError('Please enter a valid external URL.');
      return;
    }

    if (imageUrlInvalid) {
      setError('Please enter a valid image URL, or clear it.');
      goToStep(1);
      return;
    }

    try {
      setLoading(true);
      setUploadProgress(20);

      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const formData = new FormData();
      formData.append('name', form.name.trim());
      formData.append('description', form.description.trim());
      formData.append('price', Number(form.price).toString());
      formData.append('item_type', form.itemType);

      if (form.category?.id) {
        formData.append('category_id', String(form.category.id));
      } else if (form.category?.name) {
        formData.append('category_name', form.category.name);
      }

      if (form.externalUrl.trim()) {
        formData.append('external_url', form.externalUrl.trim());
      }

      if (hasValidImageUrl) {
        formData.append('image_url', imageUrlValue);
        const urlIndex = photoItems.findIndex((item) => item.source === 'url');
        if (primaryImageIndex === urlIndex) {
          formData.append('image_url_is_primary', 'true');
        }
      }

      orderedImagesForSubmit().forEach((image) => {
        formData.append('images', image.file);
      });

      setUploadProgress(45);

      const response = await fetch(`${API_BASE}/products`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      setUploadProgress(85);

      if (!response.ok) {
        const responseText = await response.text();
        let message = 'Failed to create listing.';
        try {
          const parsed = JSON.parse(responseText);
          message = parsed.error || parsed.detail || message;
        } catch (_) {
          message = responseText || message;
        }
        throw new Error(message);
      }

      await response.json();
      setUploadProgress(100);
      imagesRef.current.forEach((image) => {
        if (image.preview) URL.revokeObjectURL(image.preview);
      });
      imagesRef.current = [];
      setSuccess(`${labelForType(form.itemType)} added successfully. Redirecting to dashboard...`);
      window.setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err) {
      console.error('Error creating listing:', err);
      setError(err.message || 'Failed to create listing.');
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const renderStepOne = () => (
    <>
      <h1 className="ap-step-heading">What are you adding?</h1>
      <p className="ap-step-subhead">Pick a type, then drop in your best photo.</p>

      <div className="ap-type-grid" role="radiogroup" aria-label="Listing type">
        {TYPE_OPTIONS.map(({ value, label, Icon }) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={form.itemType === value}
            className={`ap-type-card ${form.itemType === value ? 'is-active' : ''}`}
            onClick={() => updateForm({ itemType: value })}
          >
            <Icon className="ap-type-icon" aria-hidden="true" />
            <span className="ap-type-label">{label}</span>
          </button>
        ))}
      </div>

      <div
        className={`ap-photo-drop ${dragOver ? 'is-dragging' : ''}`}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setDragOver(false);
        }}
        onDrop={handleDrop}
      >
        <div className="ap-photo-count">{photoCount} of 10</div>
        <div>
          <button
            type="button"
            className="ap-camera-button"
            aria-label="Take a photo"
            onClick={(event) => {
              event.stopPropagation();
              cameraInputRef.current?.click();
            }}
          >
            <TbCamera size={34} aria-hidden="true" />
          </button>
          <p className="ap-photo-primary">Take a photo</p>
          <p className="ap-photo-secondary">or tap to upload from library.</p>
        </div>
      </div>

      <input
        ref={cameraInputRef}
        className="ap-hidden-input"
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileInput}
      />
      <input
        ref={fileInputRef}
        className="ap-hidden-input"
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileInput}
      />

      <button
        type="button"
        className="ap-secondary-button"
        onClick={() => setShowImageUrlInput((current) => !current)}
      >
        <TbLink aria-hidden="true" />
        Use image from a URL
      </button>

      {showImageUrlInput && (
        <div className="ap-url-panel">
          <label className="ap-field-label" htmlFor="image-url">
            Image URL
          </label>
          <input
            id="image-url"
            className="ap-input"
            type="url"
            placeholder="https://example.com/photo.jpg"
            value={form.imageUrl}
            onChange={(event) => updateForm({ imageUrl: event.target.value })}
          />
          {imageUrlInvalid && <p className="ap-helper">Use a full http or https URL.</p>}
        </div>
      )}
    </>
  );

  const renderPhotoStrip = () => (
    <>
      <div className="ap-photo-strip" aria-label="Selected photos">
        {photoItems.map((item, index) => (
          <button
            key={item.id}
            type="button"
            className={`ap-thumb-tile ${index === primaryImageIndex ? 'is-main' : ''}`}
            onClick={() => setActiveThumbIndex((current) => (current === index ? null : index))}
          >
            <img src={item.preview} alt={`Selected ${index + 1}`} />
            {index === primaryImageIndex && <span className="ap-main-badge">MAIN</span>}
          </button>
        ))}

        {photoCount < MAX_IMAGES && (
          <button
            type="button"
            className="ap-add-photo-tile"
            aria-label="Add more photos"
            onClick={() => fileInputRef.current?.click()}
          >
            <TbPlus size={24} aria-hidden="true" />
          </button>
        )}
      </div>

      {activeThumbIndex !== null && photoItems[activeThumbIndex] && (
        <div className="ap-thumb-actions">
          <button
            type="button"
            className="ap-small-action"
            disabled={activeThumbIndex === primaryImageIndex}
            onClick={() => {
              setPrimaryImageIndex(activeThumbIndex);
              setActiveThumbIndex(null);
            }}
          >
            Set as main
          </button>
          <button
            type="button"
            className="ap-small-action is-danger"
            onClick={() => removePhotoItem(activeThumbIndex)}
          >
            <TbTrash aria-hidden="true" />
            Remove
          </button>
        </div>
      )}
    </>
  );

  const renderStepTwo = () => (
    <>
      {renderPhotoStrip()}

      <div className="ap-field">
        <label htmlFor="listing-name">Name</label>
        <input
          id="listing-name"
          className="ap-input"
          type="text"
          placeholder="e.g. After Hours Sticker."
          value={form.name}
          onChange={(event) => updateForm({ name: event.target.value })}
        />
      </div>

      <div className="ap-field">
        <label htmlFor="listing-price">Price</label>
        <div className="ap-price-wrap">
          <span className="ap-price-left">$</span>
          <input
            id="listing-price"
            className="ap-input"
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={form.price}
            onChange={(event) => updateForm({ price: event.target.value })}
          />
          <span className="ap-price-right">CAD</span>
        </div>
      </div>

      <div className="ap-field">
        <div className="ap-label-row">
          <span className="ap-field-label">Category</span>
        </div>
        <p className="ap-helper">Pick from your existing categories or type a new one.</p>

        <div className="ap-chip-block">
          <CategoryFilterPills
            variant="light"
            includeAll={false}
            categories={categoryOptions}
            activeSlug={form.category?.slug ?? null}
            onChange={handleCategoryChange}
            mobileCollapseStrategy="none"
            desktopCollapseThreshold={Infinity}
            barStyle={{ padding: 0 }}
            trailingItems={[
              {
                key: 'new-category',
                name: '+ New',
                dashed: true,
                active: newCategoryOpen,
                onClick: () => setNewCategoryOpen(true),
              },
            ]}
          />
        </div>

        {categoriesLoading && <p className="ap-helper">Loading categories...</p>}

        {newCategoryOpen && (
          <div className="ap-inline-panel">
            <label className="ap-field-label" htmlFor="new-category">
              New category
            </label>
            <div className="ap-new-category-row">
              <input
                id="new-category"
                className="ap-input"
                type="text"
                value={newCategoryDraft}
                onChange={(event) => setNewCategoryDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    commitNewCategory();
                  }
                }}
                placeholder="e.g. Stickers"
              />
              <button type="button" className="ap-small-action" onClick={commitNewCategory}>
                Add
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );

  const renderExternalAccordion = () => (
    <div className="ap-accordion">
      <button
        type="button"
        className="ap-accordion-button"
        aria-expanded={externalOpen}
        onClick={() => setExternalOpen((current) => !current)}
      >
        <TbExternalLink className="ap-accordion-icon" aria-hidden="true" />
        <span className="ap-accordion-label">
          Add external link <span className="ap-optional">(optional)</span>
        </span>
        <TbChevronDown className={`ap-chevron ${externalOpen ? 'is-open' : ''}`} aria-hidden="true" />
      </button>

      {externalOpen && (
        <div className="ap-inline-panel">
          <label className="ap-field-label" htmlFor="external-url">
            External URL
          </label>
          <input
            id="external-url"
            className="ap-input"
            type="url"
            placeholder="https://example.com/product-page"
            value={form.externalUrl}
            onChange={(event) => updateForm({ externalUrl: event.target.value })}
          />
          <p className="ap-helper">Add a link to your product page or external listing</p>
        </div>
      )}
    </div>
  );

  const renderPreview = () => {
    const mainPhoto = photoItems[primaryImageIndex] || photoItems[0];
    const nextCount = itemCounts[form.itemType];
    const nextNumber = Number.isFinite(Number(nextCount)) ? Number(nextCount) + 1 : '?';
    const typeClass =
      form.itemType === 'service'
        ? 'is-service'
        : form.itemType === 'experience'
          ? 'is-experience'
          : '';

    return (
      <section className="ap-preview-section">
        <p className="ap-preview-label">PREVIEW</p>
        <div className="ap-preview-card">
          <div className="ap-store-grid-preview">
            <div className="ap-preview-square">
              {mainPhoto ? (
                <img src={mainPhoto.preview} alt="" />
              ) : (
                <div className="ap-preview-placeholder">
                  <TbPhoto size={34} aria-hidden="true" />
                </div>
              )}
              <div className="ap-preview-overlay">
                <span className={`ap-preview-badge is-type ${typeClass}`}>{form.itemType}</span>
                <span className="ap-preview-badge is-number">#{nextNumber}</span>
                <div className="ap-preview-info">
                  <h3 className="ap-preview-name">{form.name.trim() || 'Listing name'}</h3>
                  <p className="ap-preview-price">${formatCurrency(form.price)}</p>
                </div>
              </div>
            </div>
          </div>
          <p className="ap-preview-helper">How it'll look on your store page.</p>
        </div>
      </section>
    );
  };

  const renderStepThree = () => (
    <>
      <div className="ap-field">
        <label htmlFor="listing-description">Description</label>
        <textarea
          ref={descriptionRef}
          id="listing-description"
          className="ap-textarea"
          placeholder="Tell shoppers what makes it special. Two or three sentences is plenty."
          value={form.description}
          onChange={(event) => updateForm({ description: event.target.value })}
        />
        <div className="ap-format-toolbar" aria-label="Formatting tools">
          <button
            type="button"
            className="ap-format-button"
            onClick={() => insertMarkdown('bold')}
            aria-label="Bold"
          >
            B
          </button>
          <button
            type="button"
            className="ap-format-button"
            onClick={() => insertMarkdown('italic')}
            aria-label="Italic"
          >
            <em>i</em>
          </button>
          <button
            type="button"
            className="ap-format-button"
            onClick={() => insertMarkdown('bullet')}
            aria-label="Bullet list"
          >
            <TbList aria-hidden="true" />
          </button>
        </div>
      </div>

      {renderExternalAccordion()}
      {renderPreview()}
    </>
  );

  const renderBottomBar = () => {
    const primaryDisabled =
      loading ||
      (step === 1 && !canContinueStep1) ||
      (step === 2 && !canContinueStep2);

    return (
      <div className="ap-bottom-bar">
        <div className="ap-bottom-inner">
          {error && <p className="ap-error">{error}</p>}
          {success && <p className="ap-success">{success}</p>}
          {!error && bottomHint && <p className="ap-bottom-hint">{bottomHint}</p>}
          {loading && uploadProgress > 0 && (
            <div className="progress mb-2" style={{ height: 6 }}>
              <div
                className="progress-bar"
                role="progressbar"
                style={{ width: `${uploadProgress}%`, backgroundColor: '#ff385c' }}
                aria-valuenow={uploadProgress}
                aria-valuemin="0"
                aria-valuemax="100"
              />
            </div>
          )}
          <div className="ap-bottom-actions">
            {step > 1 && (
              <button
                type="button"
                className="ap-back-button"
                onClick={handleBackStep}
                disabled={loading}
                aria-label="Back"
              >
                <TbArrowLeft size={22} aria-hidden="true" />
              </button>
            )}
            <button
              type="button"
              className="ap-primary-button"
              disabled={primaryDisabled}
              onClick={step === 3 ? handleSubmit : handleContinue}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                  Publishing...
                </>
              ) : step === 3 ? (
                <>
                  <TbCheck size={20} aria-hidden="true" />
                  Publish listing
                </>
              ) : (
                <>
                  Continue
                  <TbArrowRight size={20} aria-hidden="true" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="add-product-page">
      <Header />
      <div className="ap-shell">
        <header className="ap-top-nav">
          <div className="ap-nav-row">
            <button type="button" className="ap-icon-button" onClick={handleClose} aria-label="Close">
              <TbX size={24} aria-hidden="true" />
            </button>
            <h1 className="ap-nav-title">{stepTitles[step]}</h1>
            <div aria-hidden="true" />
          </div>
          <div className="ap-step-wrap">
            <div className="ap-step-segments" aria-hidden="true">
              {[1, 2, 3].map((segment) => (
                <span
                  key={segment}
                  className={`ap-step-segment ${segment <= step ? 'is-filled' : ''}`}
                />
              ))}
            </div>
            <div className="ap-step-label">Step {step} of 3</div>
          </div>
        </header>

        <main className="ap-content">
          {step === 1 && renderStepOne()}
          {step === 2 && renderStepTwo()}
          {step === 3 && renderStepThree()}
        </main>
      </div>
      {renderBottomBar()}
    </div>
  );
};

export default AddProductPage;
